import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { RouterProvider } from '@tanstack/react-router'
import { QueryClient } from '@tanstack/react-query'
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client'
import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister'
import { del, get, set } from 'idb-keyval'
import { AuthProvider as CerberusAuthProvider } from '@/lib/cerberus/AuthProvider'
import { AuthGate } from '@/lib/cerberus/AuthGate'
import { AnalyticsProvider } from '@/lib/analytics'
// NOTE: ToastProvider lives inside `<RouterProvider>` via `src/routes/__root.tsx`
// because the Pheme sanitizer (PR #260) emits drop envelopes via useTrack,
// which reads useRouterState — calling it from a sibling of <RouterProvider>
// panics on first mount. See `src/routes/__root.tsx` for the new mount point.
import { ProteusAppProvider } from '@/lib/proteus'
import { CuidariMuiProvider } from '@/lib/mui/CuidariMuiProvider'
import { getRuntimeConfig } from '@/lib/runtime-config'
import { DomainProvider } from '@/lib/domain/context'
import { ThemedShell } from '@/lib/domain/ThemedShell'
import { SpotlightProvider, SpotlightOverlay } from '@/lib/spotlight'
import { VersionGate } from '@/components/VersionGate'
import { OfflineBadge } from '@/components/OfflineBadge'
import { registerServiceWorker } from '@/lib/sw/register'
import { getSurface } from '@/lib/host'
import '@/i18n'
import { router } from './router'
import '@/styles/global.css'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
    },
  },
})

// PWA read-offline (docs/pwa-update-and-offline.md §3): persist a
// whitelist of React Query caches into IndexedDB via idb-keyval. Used by
// the SPA even before the service worker lands (PR pwa-3 is currently
// dormant) — once the SW is on, the API responses will be served from
// SW cache while React Query reads its hydrated state from here.
// Together they give a "see last-fetched data offline" experience.
//
// Cache key carries a version suffix so a schema bump can force-
// invalidate everything. Today we keep it pinned to "v1"; bump when
// the persisted shape changes.
const persister = createAsyncStoragePersister({
  storage: {
    getItem: async (k) => {
      const v = await get<string>(k)
      return v ?? null
    },
    setItem: async (k, v) => {
      await set(k, v)
    },
    removeItem: async (k) => {
      await del(k)
    },
  },
  key: 'cuidari.query-cache.v1',
})

// PII review checkpoint — read docs/pwa-update-and-offline.md §3.5
// before adding to this set. Anything not in this list is never
// persisted to IndexedDB.
const PERSIST_QUERY_KEYS: ReadonlySet<string> = new Set<string>([
  'bills',
  'transactions',
  'insights',
  'profile',
  'pheme',
])

const rootEl = document.getElementById('root')
if (!rootEl) throw new Error('No #root element found')

createRoot(rootEl).render(
  <StrictMode>
    <CuidariMuiProvider>
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister,
        // 7 days. Bills/insights change slowly enough that a week-old
        // cache is still useful as a last-resort offline view. Forced
        // refresh on every navigate stays mostly NetworkFirst once the
        // SW lands; cache is the floor, not the ceiling.
        maxAge: 1000 * 60 * 60 * 24 * 7,
        // Buster: invalidate the persisted cache when the deployed
        // bundle changes. Deploy workflow stamps `client_version.latest`
        // in runtime-config.json (PR pwa-1); we read the same SHA at
        // build time as `__APP_VERSION__`. New deploy = fresh cache.
        buster: __APP_VERSION__,
        dehydrateOptions: {
          shouldDehydrateQuery: (q) => {
            const head = q.queryKey[0]
            if (typeof head !== 'string') return false
            return PERSIST_QUERY_KEYS.has(head)
          },
        },
      }}
    >
      <CerberusAuthProvider>
        <DomainProvider>
          <ThemedShell>
            <AuthGate>
              <ProteusAppProvider>
                <AnalyticsProvider>
                  {/* T10 Frente B.1: SpotlightProvider holds the registered
                      configs + current spotlight id. Empty `configs` in this
                      PR — feature PRs (B.2, B.3) supply real configs.
                      Mounted inside AnalyticsProvider because SpotlightOverlay
                      emits `dismiss_event` via `useTrack`. See
                      docs/specs/cuidari/t10-onboarding-journey.md §9. */}
                  <SpotlightProvider configs={{}}>
                                        <RouterProvider router={router} />
                    {/* ToastProvider moved into <RouterProvider> via
                        `src/routes/__root.tsx` (hotfix 2026-05-25) — needs
                        router context for useTrack drop envelopes. */}
                    {/* PWA offline indicator — fixed top strip, only renders
                        when navigator.onLine === false. */}
                    <OfflineBadge />
                    {/* PWA version-gate: no-op until an operator sets
                        `client_version.force_update_message` or
                        `client_version.soft_update_message` in runtime-config.json.
                        See docs/pwa-update-and-offline.md §1. */}
                    <VersionGate />
                    {/* Single global spotlight overlay — portals to document.body.
                        Pages activate via `useSpotlightFromUrl(...)` or the
                        imperative `useSpotlight().activate(id)`. */}
                    <SpotlightOverlay />
                  </SpotlightProvider>
                </AnalyticsProvider>
              </ProteusAppProvider>
            </AuthGate>
          </ThemedShell>
        </DomainProvider>
      </CerberusAuthProvider>
    </PersistQueryClientProvider>
    </CuidariMuiProvider>
  </StrictMode>,
)

// Defer telemetry init until after React has mounted and the browser is
// idle. aws-rum-web and the Aegis SDK are dynamically imported so they land
// in separate chunks and do NOT inflate the initial JS payload measured by
// Lighthouse FCP/LCP. Fire-and-forget: never block UI on telemetry.
const scheduleIdle = (cb: () => void): void => {
  const idle = (window as unknown as { requestIdleCallback?: (cb: () => void) => void })
    .requestIdleCallback
  if (typeof idle === 'function') {
    idle(cb)
  } else {
    setTimeout(cb, 0)
  }
}

// Single RUM+Aegis boot chain. This replaces the two former independent
// scheduleRum callbacks (one for initRum, one for initAegisIfPossible), whose
// independent scheduling raced: the RUM SDK could start its Cognito-IDP
// credential fetches BEFORE Aegis had installed its noise filter
// (installRumNoiseFilter, run inside initAegisIfPossible), letting
// aegis-upload-sourcemaps probe sessions surface those RUM dependency hiccups
// as spurious fetch_error/anon issues. Awaiting initAegisIfPossible() before
// initRum() closes that window: the filter is guaranteed installed before RUM
// issues a single fetch.
scheduleIdle(() => {
  void import('@/lib/aegis')
    .then((mod) => mod.initAegisIfPossible())
    .then(() => Promise.all([import('@/lib/rum'), getRuntimeConfig()]))
    .then(([mod, cfg]) => mod.initRum(cfg.rum))
    .catch(() => {
      // Aegis / RUM / runtime-config errors are non-fatal to SPA boot and are
      // surfaced separately (AuthProvider / Aegis internal handling).
    })
})

// Phase 9 (M9): Core Web Vitals (LCP/INP/CLS/FCP) — dynamically imported so
// `web-vitals` (~2 kB gz) lands in its own chunk. The default emitter routes
// to `console.debug`; once the analytics relay is up, the emitter sends the
// metric envelope to the same RUM pipe used by click envelopes. Aegis alarm
// thresholds live in docs/aegis-alarms-cuidari-web.md.
scheduleIdle(() => {
  void import('@/lib/perf/webVitals').then((mod) => mod.initWebVitals())
})

// PWA service worker registration — currently a no-op (SW_ENABLED=false
// in @/lib/sw/register). Hook is wired here so flipping the flag plus
// re-adding vite-plugin-pwa lights up the SW without touching this file.
// See docs/pwa-update-and-offline.md §2 for the turn-on checklist.
registerServiceWorker()

// Janus boundary registry warm-up — fire-and-forget call to
// `GET https://janus.francisquini.ai/v1/resolve?host=<origin>` so the
// surface resolution is cached on `window.__janusSurface` for any
// component that reads it later (NotFound, RequireAdmin, etc).
//
// The router tree was built synchronously above using the hardcoded
// fallback table (since module init cannot await). This call validates
// the boot-time guess against Janus and updates the cache; on the next
// re-mount or route change, sync `getHostKind()` reads the Janus answer.
//
// Janus outages (DNS, 5xx, network) silently fall back to the table —
// `getSurface()` swallows errors so SPA boot is never blocked. See
// `src/lib/host.ts` for the resolution policy + spec D4.
scheduleIdle(() => {
  void getSurface()
})
